import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { translate } from '../../../base/i18n/functions';
import { updateSettings } from '../../../base/settings/actions';
import { shouldHideShareScreenHelper } from '../../../base/settings/functions.web';
import { toggleScreensharing } from '../../../base/tracks/mhActions';
import Checkbox from '../../../base/ui/components/web/Checkbox';
import Dialog from '../../../base/dialog/components/web/Dialog';
const SHARE_SCREEN_MP4 = 'images/share-screen.mp4';
/**
 * Prefetches the share screen video for later dialog display.
 *
 * @returns {void}
 */
function preloadShareScreenVideo() {
    if (typeof document === 'undefined' || document.querySelector(`link[href="${SHARE_SCREEN_MP4}"]`)) {
        return;
    }
    const link = document.createElement('link');
    // This helper dialog can appear well after initial page load, so use prefetch
    // instead of preload to avoid browser "preloaded but not used" warnings.
    link.rel = 'prefetch';
    link.href = SHARE_SCREEN_MP4;
    link.as = 'video';
    link.type = 'video/mp4';
    document.head.appendChild(link);
}
const useStyles = makeStyles()(() => {
    return {
        shareScreenDialog: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px 0',
            gap: '16px',
            color: '#000000', // Match old Dialog textStyles
        },
        shareScreenAnimation: {
            maxWidth: '100%',
            maxHeight: '300px',
            height: 'auto',
            borderRadius: '8px',
            objectFit: 'contain',
        },
        checkboxContainer: {
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '4px',
            color: '#000000',
        },
    };
});
/**
 * Component that displays the screen share helper dialog.
 */
const ShareScreenDialog = ({ _shouldHideShareScreenHelper, enabledState, dispatch }) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    useEffect(() => {
        preloadShareScreenVideo();
    }, []);
    /**
     * Continue the normal screen sharing flow when the user clicks continue.
     *
     * @returns {boolean}
     */
    const onContinue = useCallback(() => {
        dispatch(toggleScreensharing(enabledState));
        return true;
    }, [dispatch]);
    /**
     * Callback invoked when the hide screen share helper checkbox has been selected. This setting will be persisted in
     * the local storage, thus the dialog won't be displayed again.
     *
     * @param {Object} e - The key event to handle.
     * @returns {void}
     */
    const onSelectHideShareScreenHelper = useCallback(({ target: { checked } }) => {
        dispatch(updateSettings({ hideShareScreenHelper: checked }));
    }, [dispatch]);
    return (React.createElement(Dialog, { ok: { translationKey: 'dialog.shareScreen' }, onSubmit: onContinue, size: 'large', titleKey: t('dialog.shareScreenTitle') },
        React.createElement("div", { className: classes.shareScreenDialog },
            React.createElement("video", { autoPlay: true, preload: 'auto', className: classes.shareScreenAnimation, loop: true, muted: true, playsInline: true, src: SHARE_SCREEN_MP4, tabIndex: 0 }),
            React.createElement("div", { className: classes.checkboxContainer },
                React.createElement(Checkbox, { checked: _shouldHideShareScreenHelper, label: t('dialog.hideShareScreenHelper'), labelColor: '#000000', name: 'hide-share-screen-helper', onChange: onSelectHideShareScreenHelper })))));
};
/**
 * Maps part of the Redux state to the props of this component.
 *
 * @param {IReduxState} state - The Redux state.
 * @private
 * @returns {IProps}
 */
function _mapStateToProps(state) {
    return {
        _shouldHideShareScreenHelper: Boolean(shouldHideShareScreenHelper(state))
    };
}
export default translate(connect(_mapStateToProps)(ShareScreenDialog));
